import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, Loader2, Package } from "lucide-react";
import {
  assignToteToOrder,
  fetchToteForOrder,
  listProductionTotes,
  parseToteCodeFromText,
  TOTE_STAGE_LABELS,
} from "@/lib/productionUnitQr";
import { todayOrderBadgeClass, todayOrderBadgeLabel } from "@/lib/dailyOrder";

const PINK = "#EE2677";
const TOTE_GRID = Array.from({ length: 20 }, (_, i) => `TT${String(i + 1).padStart(2, "0")}`);

export default function KitchenUnitLabel({
  token,
  orderId,
  role = "baker",
  cakeIndex = 0,
  orderInfo = null,
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [payload, setPayload] = useState(null);
  const [totes, setTotes] = useState([]);
  const [picked, setPicked] = useState("");
  const [open, setOpen] = useState(false);

  const tote = payload?.tote;
  const order = payload?.order || orderInfo;
  const assignedCode = tote?.short_code || "";

  const refresh = useCallback(async () => {
    if (!token || !orderId) return;
    setLoading(true);
    setErr("");
    try {
      const [forOrder, list] = await Promise.all([
        fetchToteForOrder({ token, orderId, role, cakeIndex }),
        listProductionTotes({ token }).catch(() => ({ totes: [] })),
      ]);
      setPayload(forOrder?.empty ? null : forOrder);
      setTotes(list?.totes || []);
      if (forOrder?.tote?.short_code) setPicked(forOrder.tote.short_code);
    } catch (e) {
      setErr(e.message || String(e));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [token, orderId, role, cakeIndex]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAssign = async (code) => {
    const toteCode = parseToteCodeFromText(code || picked);
    if (!toteCode) {
      setErr("Pick a tray (01–20)");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const data = await assignToteToOrder({
        token,
        orderId,
        role,
        cakeIndex,
        toteCode,
      });
      setPayload(data);
      setPicked(toteCode);
      setOpen(false);
      await refresh();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const occupiedBy = (code) => {
    const row = totes.find((t) => t.short_code === code);
    if (!row || row.stage === "empty" || !row.order_number) return null;
    if (String(row.order_id) === String(orderId)) return "yours";
    return row.order_number;
  };

  if (loading) {
    return (
      <div className="ka-tote-assign ka-tote-assign--loading">
        <Loader2 className="animate-spin" size={18} aria-hidden />
        <span>Tray…</span>
      </div>
    );
  }

  return (
    <div className="ka-tote-assign">
      <style>{`
        .ka-tote-assign {
          margin: 0; border-radius: 16px; border: 1px solid rgba(235,179,169,0.55);
          background: white; overflow: hidden;
        }
        .ka-tote-assign--loading {
          display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 700;
          color: #6B7280; padding: 12px 14px;
        }
        .ka-tote-assign-toggle {
          display: flex; align-items: center; gap: 10px; width: 100%;
          border: none; background: white; padding: 12px 14px; cursor: pointer; text-align: left;
        }
        .ka-tote-assign-toggle-title {
          flex: 1; font-size: 13px; font-weight: 900; color: #111827;
        }
        .ka-tote-assign-toggle-sub {
          font-size: 10px; font-weight: 700; color: #6B7280; display: block; margin-top: 2px;
        }
        .ka-tote-assign-badge {
          font-size: 11px; font-weight: 900; letter-spacing: 0.06em;
          padding: 5px 12px; border-radius: 999px; background: ${PINK}; color: white;
        }
        .ka-tote-assign-badge--empty {
          background: #F3F4F6; color: #6B7280; border: 1px dashed #D1D5DB;
        }
        .ka-tote-assign-chevron { color: ${PINK}; transition: transform 0.15s ease; flex-shrink: 0; }
        .ka-tote-assign-chevron--open { transform: rotate(180deg); }
        .ka-tote-assign-body {
          padding: 0 14px 14px; border-top: 1px dashed rgba(238,38,119,0.2);
        }
        .ka-tote-assign-hint {
          font-size: 11px; font-weight: 600; color: #6B7280; margin: 10px 0; line-height: 1.4;
        }
        .ka-tote-assign-active {
          display: flex; align-items: center; gap: 8px; padding: 10px 12px;
          border-radius: 12px; background: #FFF7FA; border: 1px solid rgba(238,38,119,0.25); margin-bottom: 10px;
        }
        .ka-tote-assign-active strong { font-size: 16px; font-weight: 900; color: #111827; }
        .ka-tote-assign-meta { font-size: 10px; font-weight: 700; color: #6B7280; margin-top: 2px; }
        .ka-tote-grid {
          display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px;
        }
        .ka-tote-pick {
          min-height: 40px; border-radius: 10px; font-size: 11px; font-weight: 900;
          border: 1px solid #E5E7EB; background: white; color: #374151; cursor: pointer;
        }
        .ka-tote-pick--sel { border-color: ${PINK}; background: #FFF0F6; color: ${PINK}; }
        .ka-tote-pick--busy { opacity: 0.45; cursor: not-allowed; }
        .ka-tote-pick--yours { border-color: #86EFAC; background: #F0FDF4; }
        .ka-tote-assign-err { font-size: 11px; font-weight: 700; color: #B91C1C; margin-top: 8px; }
      `}</style>

      <button type="button" className="ka-tote-assign-toggle" onClick={() => setOpen((v) => !v)}>
        <Package size={20} color={PINK} aria-hidden />
        <span className="ka-tote-assign-toggle-title">
          Put in tray
          <span className="ka-tote-assign-toggle-sub">
            {assignedCode ? `Assigned ${assignedCode}` : "Tap to pick 01–20"}
          </span>
        </span>
        {assignedCode ? (
          <span className="ka-tote-assign-badge">{assignedCode}</span>
        ) : (
          <span className="ka-tote-assign-badge ka-tote-assign-badge--empty">—</span>
        )}
        <ChevronDown size={20} className={`ka-tote-assign-chevron ${open ? "ka-tote-assign-chevron--open" : ""}`} />
      </button>

      {open ? (
        <div className="ka-tote-assign-body">
          <p className="ka-tote-assign-hint">
            {role === "baker"
              ? "Base ready? Tap the tray number you put this cake in."
              : "One tray = one order. Tap to assign or change."}
          </p>

          {assignedCode ? (
            <div className="ka-tote-assign-active">
              <CheckCircle2 size={20} color={PINK} aria-hidden />
              <div>
                <strong>{assignedCode}</strong>
                <div className="ka-tote-assign-meta">
                  {tote?.stage_label || TOTE_STAGE_LABELS[tote?.stage] || "In tray"}
                  {order?.today_order_kind ? (
                    <>
                      {" · "}
                      <span className={todayOrderBadgeClass(order)}>{todayOrderBadgeLabel(order)}</span>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div className="ka-tote-grid" role="listbox" aria-label="Pick tray">
            {TOTE_GRID.map((code) => {
              const occ = occupiedBy(code);
              const isSel = picked === code || assignedCode === code;
              const disabled = busy || (occ && occ !== "yours");
              return (
                <button
                  key={code}
                  type="button"
                  className={[
                    "ka-tote-pick",
                    isSel ? "ka-tote-pick--sel" : "",
                    occ === "yours" ? "ka-tote-pick--yours" : "",
                    disabled && occ !== "yours" ? "ka-tote-pick--busy" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={disabled}
                  title={occ && occ !== "yours" ? `Tray has #${occ}` : code}
                  onClick={() => {
                    setPicked(code);
                    handleAssign(code);
                  }}
                >
                  {code.replace("TT", "")}
                </button>
              );
            })}
          </div>
          {busy ? (
            <p className="ka-tote-assign-hint" style={{ marginBottom: 0 }}>
              <Loader2 className="animate-spin inline" size={14} /> Saving…
            </p>
          ) : null}
          {err ? <p className="ka-tote-assign-err">{err}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
